import React, { useState, useMemo } from "react";
import { Layout, Typography, Card, Row, Col, Button, Input, Pagination } from "antd";
import { SearchOutlined } from "@ant-design/icons";

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const stories = [
  {
    id: 1,
    name: "Alice Mwangi",
    role: "AI Researcher",
    story: "JectHub helped me transition from academia to industry by providing hands-on AI challenges and mentorship.",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 2,
    name: "David Kimani",
    role: "Cybersecurity Analyst",
    story: "The mentorship programs and real-world projects on JectHub gave me the confidence to land my first cybersecurity job.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 3,
    name: "Fatima Abdi",
    role: "Data Scientist",
    story: "Thanks to JectHub, I built a strong portfolio that impressed my employers and opened doors to great opportunities.",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 4,
    name: "Samuel Okoro",
    role: "Machine Learning Engineer",
    story: "JectHub’s resources and community support were key in mastering machine learning and securing a role at a top tech firm.",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 5,
    name: "Nia Zulu",
    role: "Cybersecurity Specialist",
    story: "Through JectHub, I gained practical skills in threat detection that made me stand out in the job market.",
    image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 6,
    name: "Kofi Mensah",
    role: "Data Analyst",
    story: "JectHub’s data science challenges helped me sharpen my analytical skills and land a dream job.",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 7,
    name: "Amina Yusuf",
    role: "AI Developer",
    story: "The collaborative projects on JectHub boosted my coding skills and led to exciting AI development opportunities.",
    image: "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 8,
    name: "James Otieno",
    role: "Security Engineer",
    story: "JectHub’s cybersecurity training gave me the tools to protect systems and advance my career.",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 9,
    name: "Lerato Ndlovu",
    role: "Data Science Intern",
    story: "JectHub’s internship program provided me with real-world experience that kickstarted my data science career.",
    image: "https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
];

const HumansOfJectHub = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const filteredStories = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return stories.filter(
      (story) =>
        story.name.toLowerCase().includes(query) ||
        story.story.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const paginatedStories = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredStories.slice(startIndex, startIndex + pageSize);
  }, [filteredStories, currentPage]);

  return (
    <Layout>
      <Content style={{ padding: "50px", background: "#f0f2f5" }}>
        <Title level={2} style={{ fontSize: "24px", color: "#1890ff" }}>
          Humans of JectHub
        </Title>
        <Paragraph style={{ fontSize: "16px", color: "#666", marginBottom: "24px" }}>
          Meet the incredible individuals who have leveraged JectHub to transform their careers in AI, Cybersecurity, and Data Science.
        </Paragraph>

        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} md={12}>
            <Input
              placeholder="Search stories by name or description"
              prefix={<SearchOutlined />}
              size="large"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ borderRadius: "4px" }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {paginatedStories.length > 0 ? (
            paginatedStories.map((story) => (
              <Col xs={24} sm={12} md={8} lg={6} key={story.id}>
                <Card
                  hoverable
                  cover={
                    <img
                      alt={story.name}
                      src={story.image}
                      style={{ height: "150px", objectFit: "cover", borderRadius: "8px 8px 0 0" }}
                    />
                  }
                  style={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  bodyStyle={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}
                >
                  <div>
                    <Title level={4} style={{ marginBottom: "8px", color: "#333" }}>
                      {story.name}
                    </Title>
                    <Paragraph style={{ color: "#666", fontSize: "14px", marginBottom: "12px" }}>
                      <strong>{story.role}</strong>
                    </Paragraph>
                    <Paragraph style={{ color: "#666", fontSize: "14px" }}>
                      {story.story}
                    </Paragraph>
                  </div>
                  <Button
                    type="primary"
                    href="/signin"
                    style={{ backgroundColor: "#1890ff", borderColor: "#1890ff", width: "100%", marginTop: "16px" }}
                  >
                    Read Full Story
                  </Button>
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24} style={{ textAlign: "center" }}>
              <Paragraph style={{ color: "#999", fontSize: "16px" }}>
                No stories match your search. Try a different query!
              </Paragraph>
            </Col>
          )}
        </Row>

        <Pagination
          current={currentPage}
          total={filteredStories.length}
          pageSize={pageSize}
          onChange={setCurrentPage}
          style={{ marginTop: "24px", textAlign: "center" }}
          showSizeChanger={false}
        />
      </Content>
    </Layout>
  );
};

export default HumansOfJectHub;